import { useState, useMemo, useCallback, useEffect, useContext, useRef } from 'react';
import { css } from '@emotion/react';
import moment from 'moment-timezone';
import t from 'react-translate';
import { isEmpty, omit } from 'underscore';
import { AngularContext } from 'react-app';
import { Controller, useFormContext } from 'react-hook-form';

import { gray2, gray4 } from 'styles/global_defaults/colors';
import { doubleSpacing, quarterSpacing } from 'styles/global_defaults/scaffolding';

import { Timezone } from 'live_sessions/components/use-timezones';
import ClickableContainer from 'components/clickable-container';
import NvTextInput from '../inputs/nv-text-input';
import NvDropdown, { NvDropdownButtonStyle, NvDropdownHeader, NvDropdownOption } from '../inputs/nv-dropdown';
import NvIcon from '../nv-icon';
import TimezoneSelectorItem from './timezone-selector-item';

const styles = (dropdownHeight?: number) => css`
  .drop-header {
    min-width: 300px;
    border: 1px solid ${gray4};
    border-radius: ${quarterSpacing}px;
    gap: ${quarterSpacing}px;
    height: ${doubleSpacing}px;
  }

  .bs4-dropdown-menu {
    max-height: ${dropdownHeight ? `${dropdownHeight}px` : '480px'};
    overflow-y: scroll;
  }

  input {
    ::placeholder {
      color: ${gray2};
    }
  }
`;

// Removing the first and last parentheses from the offset string
/* @ngInject */
export function getFormattedOffset(tz: Timezone) {
  if (!tz) return '';
  return tz.offset.replace(/[()]/g, '');
}

type NvTimezoneSelectorProps = {
  selectedTimezone?: Timezone;
  defaultTimezone?: Timezone['longName'];
  onchange?: (newValue: Timezone) => void;
  placeholder?: string;
  className?: string;
  dropdownWidth?: number;
  dropdownMaxHeight?: number;
  withForm?: boolean;
  name?: string;
  disabled?: boolean;
};

const NvTimezoneSelector = (props: NvTimezoneSelectorProps) => {
  const {
    selectedTimezone,
    defaultTimezone,
    onchange,
    placeholder,
    className,
    dropdownWidth,
    dropdownMaxHeight,
    withForm,
    name,
    disabled,
  } = props;

  const [browserTz, setBrowserTz] = useState<Timezone>(null);
  const [selectedTz, setSelectedTz] = useState<Timezone>(null);
  const [searchInput, setSearchInput] = useState<string>('');
  const [showDropdownMenu, setShowDropdownMenu] = useState<boolean>(false);

  const { injectServices } = useContext(AngularContext);
  const [TimezonesService] = injectServices(['TimezonesService']);
  const searchInputRef = useRef(null);
  const { setValue } = useFormContext() ?? {};
  const allTimezones: Timezone[] = useMemo(() => TimezonesService.timezones, [TimezonesService.timezones]);

  // Setting the selected timezone based on the timezone long name passed as a prop
  useEffect(() => {
    if (selectedTimezone) {
      setSelectedTz(selectedTimezone);
    } else if (defaultTimezone) {
      const timezone = allTimezones.find((tz) => tz.longName === defaultTimezone);
      if (timezone) {
        setSelectedTz(timezone);
      }
    }
  }, [selectedTimezone, defaultTimezone, allTimezones]);

  // Finding the timezone details based on the browser's timezone
  // and setting it as the browser timezone
  useEffect(() => {
    const currentTimezone = TimezonesService.correctSpelling(moment.tz.guess());
    const timezone = allTimezones.find((tz) => tz.longName === currentTimezone);
    if (timezone) {
      setBrowserTz(timezone);
    }
  }, [TimezonesService, allTimezones]);

  const getHeaderItem = useCallback((title: string): NvDropdownHeader => ({
    type: 'header',
    title,
    class: 'text-small text-gray-2 font-weight-bold ml-3',
  }), []);

  const onItemClick = useCallback((tz: Timezone) => {
    setSelectedTz(tz);
    setSearchInput('');
    if (withForm) {
      setValue?.(props.name, {
        id: tz.longName,
        value: tz.offset,
      }, { shouldValidate: true, shouldDirty: true }); // set value in react hook form
    }
    onchange?.(tz);
    setShowDropdownMenu(false);
    searchInputRef?.current?.focus();
  }, [onchange, props.name, setValue, withForm]);

  // Function to get timezone items as dropdown options
  const getTimezoneItems = useCallback(() => {
    const items: NvDropdownOption[] = [];
    const groupedTimezones: Record<string, Timezone[]> = {};

    // Filter timezones based on the search input
    // If the search input is empty, show all timezones
    // If the search input is not empty, filter the timezones based on the search input
    const availableTimezones = isEmpty(searchInput)
      ? TimezonesService.timezones
      : TimezonesService.timezones.filter((tz) => (
        tz.longName.toLowerCase().includes(searchInput.toLowerCase())
        || tz.offset.toLowerCase().includes(searchInput.toLowerCase())
        || tz.shortName.toLowerCase().includes(searchInput.toLowerCase())
      ));

    if (isEmpty(availableTimezones)) {
      items.push({
        type: 'header',
        title: t.SEARCH.NO_RESULTS_FOUND(),
        class: 'text-center text-gray-2 font-weight-bold ml-3',
      });
    } else {
      availableTimezones.forEach((tz) => {
        const [region, city] = tz.longName.split('/');

        if (!groupedTimezones[region]) {
          groupedTimezones[region] = [];
        }

        groupedTimezones[region].push(tz);
      });

      const sortedRegions = Object.keys(groupedTimezones).sort();

      sortedRegions.forEach((region) => {
        items.push(getHeaderItem(region));

        (groupedTimezones[region] as any[]).forEach((tz) => (items.push({
          id: tz.id,
          type: 'custom',
          customItem: <TimezoneSelectorItem timezone={tz} onClick={onItemClick} />,
        })));
      });
    }

    return items;
  }, [TimezonesService?.timezones, getHeaderItem, onItemClick, searchInput]);

  const getBrowserTzItems = useCallback((): NvDropdownOption[] => {
    // Only show the current timezone items if it is not selected and not searching
    if (!browserTz || !isEmpty(searchInput)) return [];

    return [
      getHeaderItem(t.TIMEZONE.BROWSER_DEFAULT()),
      {
        type: 'custom',
        customItem: <TimezoneSelectorItem timezone={browserTz} onClick={onItemClick} />,
      },
      {
        type: 'divider',
        class: 'mx-3 mb-2',
      },
    ];
  }, [browserTz, searchInput, getHeaderItem, onItemClick]);

  const timezoneItems: NvDropdownOption[] = useMemo(() => [
    ...getBrowserTzItems(),
    ...getTimezoneItems(),
  ], [getBrowserTzItems, getTimezoneItems]);

  const onInputChange = (e) => {
    const { value: inputValue } = e.target;
    setSearchInput(inputValue);
    if (!showDropdownMenu) {
      // If the dropdown is not open when user types, open it
      setShowDropdownMenu(true);
    }
  };

  const getPlaceholder = useCallback(() => {
    if (searchInput) return searchInput;
    if (placeholder) return placeholder; // show provided placeholder when available
    const tz = selectedTz || browserTz;
    if (!tz) return '';
    return `${t.TIMEZONE[tz.translationKey]?.() || tz.shortName} - ${getFormattedOffset(tz)}`;
  }, [searchInput, placeholder, selectedTz, browserTz]);

  const onDropdownToggle = (isOpen: boolean) => {
    setSearchInput('');
    setShowDropdownMenu(isOpen);
  };

  // Shared dropdown rendering
  const renderDropdown = (dropdownProps = {}) => (
    <NvDropdown
      items={timezoneItems}
      buttonStyle={NvDropdownButtonStyle.CUSTOM}
      minWidth={dropdownWidth ?? 300}
      onToggle={onDropdownToggle}
      show={showDropdownMenu}
      disabled={disabled}
      customTarget={() => (
        <ClickableContainer
          className={`${className} drop-header d-flex align-items-center justify-content-between pr-2`}
        >
          <NvTextInput
            ref={searchInputRef}
            className='flex-grow-1'
            onChange={onInputChange}
            value={searchInput}
            inputClassName='border-0 bg-transparent timezone-selector-styles'
            placeholder={getPlaceholder()}
            autoComplete='off'
          />
          <NvIcon
            icon='dropdown-arrow'
            size='xss-smallest'
            className='text-gray-3'
          />
        </ClickableContainer>
      )}
      isFocusable={false}
      {...dropdownProps}
    />
  );

  if (withForm) {
    return (
      <div css={styles(dropdownMaxHeight)}>
        <Controller
          name={name}
          defaultValue={defaultTimezone}
          render={({ field }) => renderDropdown(omit(field, ['ref']))}
        />
      </div>
    );
  }

  return (
    <div css={styles}>
      {renderDropdown()}
    </div>
  );
};

export default NvTimezoneSelector;

